/**
 * AI Agent, capable of running many threads in parallel, self-reflection, and evaluation.
 * - knowledge (array of facts)
 * - instructions (array of instructions)
 * - memory (array of serialized threads)
 * - system prompt
 * - tools
 * - evaluator(s)
 * - driver(s)
 */

import { getToolMethods } from "./decorators";
import { EventSource } from "./event-source";
import { Message } from "./message";
import { Thread, type ThreadDriver } from "./thread";
import { type Tool, type ToolInterface, makeToolFromInterface } from "./tool";
import type { Evaluator } from "./evaluator";
import { traceable } from "./tracing";

export interface AgentKnowledge {
  fact: string;
  source?: string;
  timestamp?: Date;
}

export interface AgentInstruction {
  instruction: string;
  priority: number;
  context?: string;
}

export interface AgentMemory {
  threadId: string;
  messages: Message[];
  timestamp: Date;
  metadata?: Record<string, any>;
}

export interface AgentOptions {
  onComplete?: (agent: Agent) => Promise<void>;
  knowledge?: AgentKnowledge[];
  instructions?: AgentInstruction[];
  memory?: AgentMemory[];
  systemPrompt?: string;
  tools?: ToolInterface[];
  driver: ThreadDriver;
  evaluators?: Evaluator[];
  trace?: boolean;
  traceMetadata?: Record<string, unknown>;
}

export class Agent extends EventSource {
  public knowledge: AgentKnowledge[];
  public instructions: AgentInstruction[];
  public memory: AgentMemory[];
  public systemPrompt: string;
  public tools: Tool[];
  public driver: ThreadDriver;
  public activeThreads: Map<string, Thread>;
  public evaluators: Evaluator[];
  private readonly trace: boolean;
  private readonly traceMetadata?: Record<string, unknown>;

  constructor({
    knowledge = [],
    instructions = [],
    memory = [],
    systemPrompt = "",
    tools = [],
    driver,
    evaluators = [],
    trace = false,
    traceMetadata,
  }: AgentOptions) {
    super();
    this.knowledge = knowledge;
    this.instructions = instructions.sort((a, b) => b.priority - a.priority);
    this.memory = memory;
    this.systemPrompt = systemPrompt;
    this.tools = [
      ...tools.map(makeToolFromInterface),
      ...this.getDecoratedTools(),
    ];
    this.driver = driver;
    this.activeThreads = new Map();
    this.evaluators = evaluators;
    this.trace = trace;
    this.traceMetadata = traceMetadata;

    this.tools.forEach((tool) => {
      this.forwardEvents(tool, { agent: this });
    });
  }

  /**
   * Get tools defined using decorators on the agent class
   */
  private getDecoratedTools(): Tool[] {
    return getToolMethods(this).map(makeToolFromInterface);
  }

  /**
   * Add a new tool to the agent
   * @param tool The tool interface to add
   * @returns The created Tool instance
   */
  public addTool(tool: ToolInterface): Tool {
    const newTool = makeToolFromInterface(tool);
    this.tools.push(newTool);
    this.forwardEvents(newTool, { agent: this });
    this.emit("agent.tool-added", { agent: this, tool: newTool });
    return newTool;
  }

  /**
   * Execute the agent with a prompt
   */
  executeAgent(prompt: string): void {
    const thread = this.createThread({
      prompt,
    });
    const evaluators = [...this.evaluators];
    const completedEvaluators: Evaluator[] = [];
    let currentEvaluator: Evaluator | undefined;

    const handleEvaluators = () => {
      // run evaluators
      for (const evaluator of evaluators) {
        if (completedEvaluators.includes(evaluator)) {
          continue;
        }
        // ignore activity when an evaluator is already running
        if (currentEvaluator) {
          continue;
        }
        currentEvaluator = evaluator;
        currentEvaluator({
          thread,
          agent: this,
          complete: () => {
            completedEvaluators.push(evaluator);
            currentEvaluator = undefined;

            // handle next evaluator
            handleEvaluators();
          },
        });
        break;
      }

      // done with evaluation, should not leave an orphaned event listener
      if (!currentEvaluator) {
        thread.off("thread.response", handleEvaluators);
      }
    };

    thread.on("thread.response", handleEvaluators);
  }

  /**
   * Create a new thread with optional messages or prompt
   */
  public createThread(props: {
    messages?: Message[];
    prompt?: string;
    systemPrompt?: string;
    onResponse?: (message: Message) => Promise<void>;
  }): Thread {
    let threadMessages: Message[] = [];

    if (props.messages) {
      threadMessages = props.messages;
    } else {
      if (!props.prompt || props.prompt.trim() === "") {
        throw new Error("Either messages or a non-empty prompt must be provided");
      }

      const systemMessage = new Message({
        role: "system",
        cache: true,
        content: this.buildSystemPrompt(props.systemPrompt),
      });

      const promptMessage = new Message({
        role: "user",
        cache: true,
        content: props.prompt,
      });

      threadMessages = [systemMessage, promptMessage];
    }

    const thread = new Thread({
      messages: threadMessages,
      tools: this.tools,
      toolChoice: "auto",
      driver: this.driver,
    });

    if (props.onResponse) {
      thread.onResponse(props.onResponse);
    }

    this.activeThreads.set(thread.id, thread);

    // Forward thread events with agent context
    this.forwardEvents(thread, { agent: this });

    this.emit("agent.thread-added", { agent: this, thread });

    return thread;
  }

  /**
   * Get an active thread by its ID
   */
  public getThread(threadId: string): Thread | undefined {
    return this.activeThreads.get(threadId);
  }

  /**
   * Get all active threads
   */
  public getActiveThreads(): Map<string, Thread> {
    return new Map(this.activeThreads);
  }

  /**
   * Close a thread and store its messages in memory
   */
  public closeThread(threadId: string): void {
    const thread = this.activeThreads.get(threadId);
    if (thread) {
      this.memory.push({
        threadId,
        messages: thread.all(),
        timestamp: new Date(),
      });
      this.activeThreads.delete(threadId);
      this.emit("agent.thread-removed", { agent: this, thread });
    }
  }

  /**
   * Add a new piece of knowledge to the agent
   */
  public addKnowledge(knowledge: AgentKnowledge): void {
    this.knowledge.push({
      ...knowledge,
      timestamp: knowledge.timestamp || new Date(),
    });
    this.emit("agent.knowledge-added", { agent: this, knowledge });
  }

  /**
   * Add a new instruction to the agent
   */
  public addInstruction(instruction: AgentInstruction): void {
    this.instructions.push(instruction);
    this.instructions.sort((a, b) => b.priority - a.priority);
    this.emit("agent.instruction-added", { agent: this, instruction });
  }

  /**
   * Get all tools available to the agent
   */
  public getTools(): Tool[] {
    return [...this.tools];
  }

  /**
   * Get all knowledge available to the agent
   */
  public getKnowledge(): AgentKnowledge[] {
    return [...this.knowledge];
  }

  /**
   * Get all instructions available to the agent
   */
  public getInstructions(): AgentInstruction[] {
    return [...this.instructions];
  }

  /**
   * Get all memory entries available to the agent
   */
  public getMemory(): AgentMemory[] {
    return [...this.memory];
  }

  public setSystemPrompt(systemPrompt: string): void {
    this.systemPrompt = systemPrompt;
  }

  /**
   * Build the system prompt combining knowledge, instructions, and base prompt
   */
  private buildSystemPrompt(systemPrompt?: string): string {
    const parts: string[] = [systemPrompt || this.systemPrompt];

    if (this.knowledge.length > 0) {
      parts.push("\n# KNOWLEDGE:");
      this.knowledge.forEach((k) => parts.push(`- ${k.fact}`));
    }

    if (this.instructions.length > 0) {
      parts.push("\n# INSTRUCTIONS:");
      this.instructions.forEach((i) =>
        parts.push(`${i.priority}. ${i.instruction}`)
      );
    }

    return parts.join("\n");
  }

  /**
   * Execute a task with tracing enabled
   * @param name The name of the trace
   * @param task The task to execute
   * @param metadata Additional metadata for tracing (e.g. runId, tags)
   */
  protected async executeTask<T>(
    name: string,
    task: () => Promise<T>,
    metadata?: Record<string, unknown>
  ): Promise<T> {
    return this.trace 
      ? traceable(task, { 
          name,
          ...this.traceMetadata,
          ...metadata,
        })()
      : task();
  }
}
